import { Suspense, useEffect, useRef } from 'react';
import { useAuthContext, useMutation } from '/src/hooks';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ONBOARDING_FLOWS, TECH_RECOMMENDATIONS } from './constants';
import { OnboardingSetup } from './pages/setup';
import { CheckSiteStep } from './pages/check-site';
import { InstructionsStep } from './pages/instructions';
import { CheckInstallStep } from './pages/check-install';
import { upsertUserOnboardingMutation } from './mutations';
import { normalizeOnboardingSiteUrl, withQueryParams } from './utils';

export function canUseOnboarding(currentUser, sessionInfo) {
  return currentUser?.userId === currentUser?.guestId || sessionInfo?.isImpersonated;
}

export function OnboardingWizard() {
  const { currentUser, sessionInfo } = useAuthContext();
  const onboarding = currentUser?.onboarding ?? {};
  const { step } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasTrackedStartRef = useRef(Boolean(onboarding.startedAt));
  const { mutateAsync: upsertUserOnboarding } = useMutation(upsertUserOnboardingMutation);
  const flowParam = searchParams.get('flow');
  const recommendationParam = searchParams.get('recommendation');
  const techParam = searchParams.get('tech');
  const siteUrlParam = normalizeOnboardingSiteUrl(searchParams.get('site_url'));
  const heatmapUrlParam = normalizeOnboardingSiteUrl(searchParams.get('heatmap_url'));
  const flow = flowParam ?? onboarding.flow;
  const queryString = searchParams.toString();
  const canAccessOnboarding = canUseOnboarding(currentUser, sessionInfo);

  const recoveredSearchParams = new URLSearchParams(searchParams);

  if (siteUrlParam) {
    recoveredSearchParams.set('site_url', siteUrlParam);
  }

  if (heatmapUrlParam) {
    recoveredSearchParams.set('heatmap_url', heatmapUrlParam);
  }

  if (!flowParam && onboarding.flow) {
    recoveredSearchParams.set('flow', onboarding.flow);
  }

  if (!siteUrlParam && onboarding.siteUrl) {
    recoveredSearchParams.set('site_url', normalizeOnboardingSiteUrl(onboarding.siteUrl));
  }

  if (flow === ONBOARDING_FLOWS.HEATMAPS && !heatmapUrlParam) {
    const recoveredHeatmapUrl =
      normalizeOnboardingSiteUrl(onboarding.heatmapUrl) ||
      normalizeOnboardingSiteUrl(recoveredSearchParams.get('site_url')) ||
      normalizeOnboardingSiteUrl(onboarding.siteUrl);

    if (recoveredHeatmapUrl) {
      recoveredSearchParams.set('heatmap_url', recoveredHeatmapUrl);
      const recoveredHeatmapDomain = normalizeOnboardingSiteUrl(recoveredHeatmapUrl).split('/')[0];
      const recoveredSiteDomain = normalizeOnboardingSiteUrl(recoveredSearchParams.get('site_url'))?.split('/')[0];

      if (recoveredSiteDomain && recoveredHeatmapDomain !== recoveredSiteDomain) {
        recoveredSearchParams.set('site_url', recoveredHeatmapDomain);
      }
    }
  }

  const recoveredQueryString = recoveredSearchParams.toString();

  useEffect(() => {
    if (!canAccessOnboarding) return;

    if (recoveredQueryString !== queryString) {
      const targetPath = step ? `/onboarding/${step}` : '/onboarding';
      navigate(withQueryParams(targetPath, recoveredQueryString), { replace: true });
      return;
    }

    if (!onboarding.startedAt && !hasTrackedStartRef.current) {
      hasTrackedStartRef.current = true;

      void upsertUserOnboarding({ startedAt: Math.floor(Date.now() / 1000) }).catch(() => {
        hasTrackedStartRef.current = false;
      });
    }

    if (step === 'instructions' && siteUrlParam && !recommendationParam && !techParam) {
      navigate(withQueryParams('/onboarding/check-site', recoveredQueryString), { replace: true });
      return;
    }

    if (!step && flow === ONBOARDING_FLOWS.HEATMAPS) {
      navigate(withQueryParams('/onboarding/setup', recoveredQueryString), { replace: true });
    } else if (!step) {
      const nextSearchParams = new URLSearchParams(recoveredQueryString);
      const recoveredSiteUrl = normalizeOnboardingSiteUrl(nextSearchParams.get('site_url'));

      if (!recoveredSiteUrl) {
        nextSearchParams.set('recommendation', TECH_RECOMMENDATIONS.NONE);
        navigate(withQueryParams('/onboarding/instructions', nextSearchParams.toString()), { replace: true });
        return;
      }

      navigate(withQueryParams('/onboarding/check-site', recoveredQueryString), { replace: true });
    }
  }, [
    flow,
    canAccessOnboarding,
    navigate,
    onboarding.startedAt,
    queryString,
    recoveredQueryString,
    step,
    siteUrlParam,
    recommendationParam,
    techParam,
    upsertUserOnboarding,
  ]);

  if (!canAccessOnboarding) {
    return <Navigate to="/" replace />;
  }

  const StepComponent = () => {
    switch (step) {
      case 'check-site':
        return <CheckSiteStep />;
      case 'setup':
        return <OnboardingSetup />;
      case 'instructions':
        return <InstructionsStep />;
      case 'check-install':
        return <CheckInstallStep />;
      default:
        return step ? <Navigate to="/onboarding" replace /> : null;
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="flex h-full w-full flex-col bg-white-lilac-500">
        <StepComponent />
      </div>
    </Suspense>
  );
}
